import React, { useMemo, useState } from 'react';
import { Row, Col, Form, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faTrashCan,
    faFileInvoice,
    faCircleExclamation,
    faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const SHOPS = ['Rades 1', 'Rades 2'];
const DESIGNATIONS = ['Monture optique', 'Verre optique', 'Lentille de contact'];

const formatDT = (value) => `${Number(value || 0).toFixed(3)} DT`;

const GenerateInvoice = () => {
    const [invoiceDetails, setInvoiceDetails] = useState({
        items: [],
    });

    const [shop, setShop] = useState('');
    const [invoiceN, setInvoiceN] = useState('');
    const [clientName, setClientName] = useState('');

    const [quantity, setQuantity] = useState('');
    const [designation, setDesignation] = useState('');
    const [pricePerUnit, setPricePerUnit] = useState('');
    const [itemError, setItemError] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [pdfSrc, setPdfSrc] = useState('');
    const [pdfDoc, setPdfDoc] = useState(null);
    const [pdfFileName, setPdfFileName] = useState('');

    const handleModalShow = () => setShowModal(true);
    const handleModalClose = () => setShowModal(false);

    const total = useMemo(
        () =>
            invoiceDetails.items.reduce(
                (sum, item) => sum + parseInt(item.quantity, 10) * parseFloat(item.pricePerUnit),
                0
            ),
        [invoiceDetails.items]
    );

    const missingInvoiceFields = !shop || !invoiceN || !clientName.trim();
    const canGenerate = !missingInvoiceFields && invoiceDetails.items.length > 0;

    const addRow = () => {
        if (!quantity || !designation || !pricePerUnit) {
            setItemError('Renseignez la quantité, la désignation et le prix avant d’ajouter la ligne.');
            return;
        }

        const newRow = { quantity, designation, pricePerUnit };

        setInvoiceDetails((prevDetails) => ({
            ...prevDetails,
            items: [...prevDetails.items, newRow],
        }));

        setQuantity('');
        setDesignation('');
        setPricePerUnit('');
        setItemError('');
    };

    const removeRow = (index) => {
        setInvoiceDetails((prevDetails) => ({
            ...prevDetails,
            items: prevDetails.items.filter((_, i) => i !== index),
        }));
    };

    const numberToFrenchText = (number) => {
        const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
        const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
        const tens = ['', 'dix', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];

        if (number < 10) {
            return units[number];
        } else if (number < 20) {
            return teens[number - 10];
        } else if (number < 100) {
            const unit = number % 10;
            const ten = Math.floor(number / 10);
            if (unit === 0) {
                return tens[ten];
            } else if (unit === 1) {
                if (ten === 8) {
                    return 'dix-' + units[unit];
                } else {
                    return teens[unit];
                }
            } else if (ten === 7 || ten === 9) {
                return tens[ten - 1] + '-' + (ten === 7 && unit === 1 ? 'et-' : '') + units[unit];
            } else {
                return tens[ten] + (unit === 1 ? ' et' : '-') + units[unit];
            }
        } else if (number < 1000) {
            const hundred = Math.floor(number / 100);
            const rest = number % 100;
            return (hundred === 1 ? 'cent' : units[hundred] + ' cent') + (rest !== 0 ? ' ' + numberToFrenchText(rest) : '');
        } else if (number < 1000000) {
            const thousand = Math.floor(number / 1000);
            const rest = number % 1000;
            return (thousand === 1 ? 'mille' : numberToFrenchText(thousand) + ' mille') + (rest !== 0 ? ' ' + numberToFrenchText(rest) : '');
        }
        return 'Number out of range';
    };

    const generateInvoice = () => {
        var items = invoiceDetails.items;
        var doc = new jsPDF('portrait', 'mm', 'a5');
        var pageWidth = doc.internal.pageSize.getWidth();
        const logo_go_line = 'https://i.imgur.com/pmmKaJt.jpg';
        doc.addImage(logo_go_line, 'JPEG', 10, 10, pageWidth - 20, 15);

        doc.setFontSize(7);
        doc.text("GRAND OPTICAL", 13, 28);
        if (shop == 'Rades 1') {
            doc.text("MF 409167PAC000", 13, 31);
            doc.text("RC A1324001997", 13, 34);

            doc.text("1 Rue Habib Thameur, Radés, Ben Arous", 13, 37);
            doc.text("Tel: 71 442 463 - Fax: 71 445 717", 13, 40);
        } else if (shop == 'Rades 2') {
            doc.text("MF 1418052MPM000", 13, 31);
            doc.text("RC C02238722015", 13, 34);

            doc.text("23 Bis Boulevard Kheireddine Pacha, Rades, Ben Arous", 13, 37);
            doc.text("Tel: 71 460 261 - Fax: 71 445 717", 13, 40);
        }

        doc.setFontSize(12);
        var today = new Date();
        var dd = String(today.getDate()).padStart(2, '0');
        var mm = String(today.getMonth() + 1).padStart(2, '0');
        var yyyy = today.getFullYear();
        today = `${dd}/${mm}/${yyyy}`;
        var todayText = `Tunis, le ${today}`;
        var todayTextWidth = doc.getStringUnitWidth(todayText) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        doc.text(todayText, pageWidth - todayTextWidth - 16, 35);

        var text1 = `Facture #${String(invoiceN).padStart(6, '0')}`;
        var text1Width = doc.getStringUnitWidth(text1) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        doc.text(text1, pageWidth - text1Width - 16, 40);

        doc.text("Client: ", 15, 50);

        doc.setFont("helvetica", "bold");
        var name = clientName;
        doc.text(name, 30, 50);
        doc.setFont("helvetica", "normal");

        var data = [
            ["Quantité", "Désignation", "P.U. TTC", "P.T. TTC"]
        ];

        var totalTTC = 0;
        items.forEach(item => {
            var totalPrice = (parseInt(item.quantity) * parseFloat(item.pricePerUnit));
            totalTTC += parseFloat(totalPrice);
            data.push([item.quantity, item.designation, parseFloat(item.pricePerUnit).toFixed(3), totalPrice.toFixed(3)]);
        });

        var startY = 55;
        autoTable(doc, {
            head: [data[0]],
            body: data.slice(1),
            startY: startY,
            headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
            styles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
            alternateRowStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
            margin: { left: 15, right: 15 },
            columnStyles: { 0: { columnWidth: (pageWidth - 30) / 6 }, 1: { columnWidth: (pageWidth - 30) / 2 }, 2: { columnWidth: (pageWidth - 30) / 6 }, 3: { columnWidth: (pageWidth - 30) / 6 } }
        });

        var text = "Arrêtée la présente facture à la somme de:";
        var amount = totalTTC.toFixed(3);
        var dinars = Math.floor(amount);
        var millimes = Math.round((amount - dinars) * 1000);

        var value = (dinars ? numberToFrenchText(dinars) + ` dinar${dinars != 1 ? 's' : ''}` : '') +
            (dinars && millimes ? ' et ' : '') +
            (millimes ? numberToFrenchText(millimes) + ` millime${millimes != 1 ? 's' : ''}.` : '.');
        value = value.charAt(0).toUpperCase() + value.slice(1);

        data = [
            [text + "\n\n" + value, "Total HT\nTVA(19%)\nTotal TTC", (totalTTC * 81 / 100).toFixed(3) + "\n" + (totalTTC * 19 / 100).toFixed(3) + "\n" + totalTTC.toFixed(3) + "\n"]
        ];

        doc.setFontSize();

        startY = doc.lastAutoTable.finalY;
        autoTable(doc, {
            body: data,
            startY: startY,
            styles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
            alternateRowStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
            margin: { left: 15, right: 15 },
            columnStyles: { 0: { columnWidth: 4 * (pageWidth - 30) / 6 }, 2: { columnWidth: (pageWidth - 30) / 6 }, 3: { columnWidth: (pageWidth - 30) / 6 } },
        });

        var fileName = `${shop}_${today}_${name}_${String(invoiceN).padStart(6, '0')}.pdf`.replace(/ /g, '-');

        setPdfDoc(doc);
        setPdfFileName(fileName);
        setPdfSrc(doc.output('datauristring'));
        handleModalShow();
    };

    const downloadInvoice = () => {
        if (pdfDoc) {
            pdfDoc.save(pdfFileName);
        }
    };

    const digitsOnly = (value) => value.replace(/^0+(?=\d)/, '').replace(/[^0-9]/g, '');
    const decimalOnly = (value) => value.replace(/^0+(?=\d)/, '').replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

    return (
        <>
            <div className="page-header">
                <span className="page-eyebrow">
                    <FontAwesomeIcon icon={faFileInvoice} /> Facturation
                </span>
                <h1 className="page-title">Nouvelle facture</h1>
                
            </div>

            <div className="panel">
                <div className="panel-section">
                    <div className="section-heading">
                        <span className="section-index">1</span>
                        <h2>Point de vente &amp; facture</h2>
                    </div>
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Label className="field-label">Point de vente</Form.Label>
                            <Form.Select
                                id="shop"
                                value={shop}
                                onChange={(e) => setShop(e.target.value)}
                            >
                                <option value="">Choisir un point de vente</option>
                                {SHOPS.map((s) => (
                                    <option key={s}>{s}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={6}>
                            <Form.Label className="field-label">Numéro de facture</Form.Label>
                            <Form.Control
                                type="text"
                                inputMode="numeric"
                                placeholder="Ex. 1024"
                                value={invoiceN}
                                onChange={(e) => setInvoiceN(digitsOnly(e.target.value))}
                            />
                        </Col>
                    </Row>
                </div>

                <div className="panel-section">
                    <div className="section-heading">
                        <span className="section-index">2</span>
                        <h2>Client</h2>
                    </div>
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Label className="field-label">Nom du client</Form.Label>
                            <Form.Control
                                type="text"
                                id="name"
                                placeholder="Nom et prénom"
                                value={clientName}
                                onChange={(e) => setClientName(e.target.value)}
                            />
                        </Col>
                    </Row>
                </div>

                <div className="panel-section">
                    <div className="section-heading">
                        <span className="section-index">3</span>
                        <h2>Articles</h2>
                    </div>

                    <Row className="g-3 align-items-end">
                        <Col xs={6} md={2}>
                            <Form.Label className="field-label">Quantité</Form.Label>
                            <Form.Control
                                type="text"
                                inputMode="numeric"
                                placeholder="0"
                                value={quantity}
                                onChange={(e) => setQuantity(digitsOnly(e.target.value))}
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="field-label">Désignation</Form.Label>
                            <Form.Select
                                id="designation"
                                value={designation}
                                onChange={(e) => setDesignation(e.target.value)}
                            >
                                <option value="">Choisir un article</option>
                                {DESIGNATIONS.map((d) => (
                                    <option key={d}>{d}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={6} md={4}>
                            <Form.Label className="field-label">Prix unitaire (DT)</Form.Label>
                            <Form.Control
                                type="text"
                                inputMode="decimal"
                                placeholder="0.000"
                                value={pricePerUnit}
                                onChange={(e) => setPricePerUnit(decimalOnly(e.target.value))}
                            />
                        </Col>
                        <Col xs={12} md={2}>
                            <Button
                                className="btn-primary btn-icon-add"
                                onClick={addRow}
                                aria-label="Ajouter la ligne"
                                title="Ajouter la ligne"
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </Button>
                        </Col>
                    </Row>

                    {itemError && (
                        <div className="field-alert">
                            <FontAwesomeIcon icon={faCircleExclamation} />
                            {itemError}
                        </div>
                    )}

                    <div className="items-table-wrap mt-4">
                        {invoiceDetails.items.length ? (
                            <table className="items-table">
                                <thead>
                                    <tr>
                                        <th className="num">Qté</th>
                                        <th>Désignation</th>
                                        <th className="num">P.U. TTC</th>
                                        <th className="num">P.T. TTC</th>
                                        <th aria-label="Actions" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceDetails.items.map((item, index) => (
                                        <tr key={index}>
                                            <td className="num">{item.quantity}</td>
                                            <td>{item.designation}</td>
                                            <td className="num">{formatDT(item.pricePerUnit)}</td>
                                            <td className="num">
                                                {formatDT(parseInt(item.quantity, 10) * parseFloat(item.pricePerUnit))}
                                            </td>
                                            <td className="num">
                                                <button
                                                    type="button"
                                                    className="btn-ghost-danger"
                                                    onClick={() => removeRow(index)}
                                                    aria-label={`Supprimer la ligne ${item.designation}`}
                                                    title="Supprimer la ligne"
                                                >
                                                    <FontAwesomeIcon icon={faTrashCan} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="items-empty">
                                <div><FontAwesomeIcon icon={faFileInvoice} size="lg" /></div>
                                Aucun article ajouté pour le moment.
                            </div>
                        )}
                    </div>

                    {invoiceDetails.items.length > 0 && (
                        <div className="totals-strip">
                            <div className="totals-row grand">
                                <span>Total TTC</span>
                                <span>{formatDT(total)}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="action-bar">
                    <span className={`status-msg ${missingInvoiceFields ? 'warn' : ''}`}>
                        {missingInvoiceFields
                            ? 'Complétez le point de vente, le numéro de facture et le client.'
                            : invoiceDetails.items.length === 0
                                ? 'Ajoutez au moins un article pour continuer.'
                                : 'Prêt à générer la facture.'}
                    </span>
                    <Button
                        className="btn-accent"
                        disabled={!canGenerate}
                        onClick={generateInvoice}
                    >
                        <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                        Générer la facture
                    </Button>
                </div>
            </div>

            <Modal show={showModal} onHide={handleModalClose} size="lg" centered className="pdf-modal">
                <ModalHeader closeButton>
                    <Modal.Title>Aperçu de la facture</Modal.Title>
                </ModalHeader>
                <ModalBody className="p-0">
                    <iframe
                        id="pdfPreview"
                        title="Aperçu PDF"
                        style={{ width: '100%', height: '500px', border: 'none' }}
                        src={pdfSrc}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline-secondary" onClick={handleModalClose}>
                        Fermer
                    </Button>
                    <Button className="btn-primary" onClick={downloadInvoice}>
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        Télécharger
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default GenerateInvoice;